/**
 * TimelinePreviewPlayer
 *
 * Shared multi-track preview wrapper used by:
 *  - Canvas preview (VideoEditorNode) — readOnly, with custom chrome overlays
 *  - (future) Anywhere we want to embed the same playback engine outside the
 *    Timeline Editor modal.
 *
 * Owns:
 *  - Conversion of edge-resolved video/audio refs → TimelineClip[] /
 *    TimelineAudioClip[]
 *  - useClipPreloader (blob URLs)
 *  - blob-based duration probing
 *  - useTimelineState (preview playback state)
 *  - in_video_config restore from the VideoEditor node
 *  - useTimelinePlayback (RAF playhead loop)
 *  - useAudioPool (multi-track audio, with globalMuted)
 *  - <VideoPreviewPlayer> (premounted <video> per clip; zero-gap transitions)
 *
 * Exposes a renderOverlays(controls) render-prop so the host can inject its
 * own clip badge / mute / fullscreen / bottom bar.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { VideoPreviewPlayer, type TimelineVideoPreviewPlayerHandle } from './VideoPreviewPlayer'
import { LegacyTextPreview } from './LegacyTextPreview'
import { DEFAULT_TEXT_FORMAT } from './useLegacyTextTracks'
import { useTimelineState } from './useTimelineState'
import { useTimelinePlayback } from './useTimelinePlayback'
import { useClipPreloader } from './useClipPreloader'
import { useAudioPool } from './useAudioPool'
import {
  applyRealDurationsToTimeline,
  buildInitialTimelineAudioClips,
  computeTimelineDuration,
  isPristineTimelineRenderConfig,
  parseTimelineRenderConfig,
  timelineClipDurationOrDefault,
  timelineConfigTraceHash,
} from './timelineUtils'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createNadDebug } from '../../../../utils/debugLog'
import { emitTimelineNadEvent } from '../../audio-edit/nadTimelineLog'
import type { TimelineAudioClip, TimelineClip, TimelineRenderConfig } from './types'

const TIMELINE_SOURCE_NAMESPACE = 'dw:video-editor:timeline-source-of-truth'
const timelineSourceLog = createNadDebug(TIMELINE_SOURCE_NAMESPACE)
const previewDurationLog = createNadDebug('dw:video-editor-preview-duration')
const emittedPreviewConfigTraceKeys = new Set<string>()

type TimelineSourceEvent = { event: string } & Record<string, string | number | boolean | null | object | undefined>

function emitTimelineSourceEvent(data: TimelineSourceEvent): void {
  timelineSourceLog(data)
  emitTimelineNadEvent(TIMELINE_SOURCE_NAMESPACE, data)
}

export interface TimelinePreviewClipRef {
  id: string
  path: string
  name: string
  duration?: number
}

export interface TimelinePreviewAudioRef extends TimelinePreviewClipRef {
  role?: 'original' | 'replacement' | 'sfx' | 'music'
}

export interface TimelinePreviewControls {
  isPlaying: boolean
  togglePlayPause: () => void
  playheadPosition: number
  totalDuration: number
  setPlayhead: (position: number) => void
  currentClipIndex: number | null
  currentClipName: string | null
  videoCount: number
  audioCount: number
  isMuted: boolean
  toggleMute: () => void
  resumeAudioContexts: () => void
  /** Ref pointing at the wrapper div (used by host for native fullscreen). */
  previewElementRef: React.RefObject<HTMLDivElement>
}

interface TimelinePreviewPlayerProps {
  nodeId: string
  videoClips: TimelinePreviewClipRef[]
  audioClips: TimelinePreviewAudioRef[]
  /** Host mode marker. The preview never persists timeline state. */
  readOnly?: boolean
  /** Canonical saved timeline config from the VideoEditor node input. */
  timelineConfig?: TimelineRenderConfig
  /** When true, hide the built-in top-left clip badge (host renders its own). */
  hideClipBadge?: boolean
  /** Initial mute state for embedded preview media. */
  initialMuted?: boolean
  /** Render-prop for chrome overlays (clip badge, mute, fullscreen, bottom bar). */
  renderOverlays?: (controls: TimelinePreviewControls) => ReactNode
}

export function TimelinePreviewPlayer({
  nodeId,
  videoClips,
  audioClips,
  readOnly = false,
  timelineConfig,
  hideClipBadge = false,
  initialMuted = false,
  renderOverlays,
}: TimelinePreviewPlayerProps) {
  const projectId = useProjectId()
  const [isMuted, setIsMuted] = useState(initialMuted)
  const previewElementRef = useRef<HTMLDivElement>(null)
  const videoPreviewRef = useRef<TimelineVideoPreviewPlayerHandle>(null)

  // ── Convert refs into TimelineClip / TimelineAudioClip ──────────────
  const initialClips: TimelineClip[] = useMemo(() => {
    let nextStartOffset = 0
    return videoClips.map((clip, index) => {
      const duration = timelineClipDurationOrDefault(clip.duration)
      const timelineClip: TimelineClip = {
        id: clip.id,
        sourceNodeId: clip.id,
        sourcePath: clip.path,
        originalDuration: duration,
        trimStart: 0,
        trimEnd: 0,
        effectiveDuration: duration,
        order: index,
        startOffset: nextStartOffset,
        trackIndex: 0,
        label: clip.name,
        speed: 1.0,
        volume: 1.0,
        muted: false,
      }
      nextStartOffset += duration
      return timelineClip
    })
  }, [videoClips])

  const initialAudioClips: TimelineAudioClip[] = useMemo(
    () => buildInitialTimelineAudioClips(audioClips),
    [audioClips],
  )

  const videoClipsKey = videoClips.map((c) => `${c.id}:${c.path}`).join('|')
  const audioFileKey = audioClips.map((a) => `${a.id}:${a.path}`).join('|')

  const {
    timelineState,
    setPlayhead,
    togglePlayPause,
    restoreState,
    refreshTimelineState,
  } = useTimelineState()

  const timelineStateRef = useRef(timelineState)
  timelineStateRef.current = timelineState

  // ── Preload all source paths as blobs ─────────────────────────────
  const allSourcePaths = useMemo(() => {
    const set = new Set<string>()
    for (const c of videoClips) set.add(c.path)
    for (const a of audioClips) set.add(a.path)
    return Array.from(set)
  }, [videoClips, audioClips])

  const preloader = useClipPreloader({ projectId, sourcePaths: allSourcePaths })

  // ── Audio pool (with global mute override) ────────────────────────
  const audioPool = useAudioPool({
    audioClips: timelineState.audioClips,
    playheadPosition: timelineState.playheadPosition,
    isPlaying: timelineState.isPlaying,
    preloader,
    globalMuted: isMuted,
  })

  // ── Probe real durations from blob URLs ────────────────────────────
  const loadedDurationsRef = useRef<Map<string, number>>(new Map())
  const [durationsLoaded, setDurationsLoaded] = useState(false)
  const prevKeysRef = useRef(`${videoClipsKey}|${audioFileKey}`)

  useEffect(() => {
    const currentKeys = `${videoClipsKey}|${audioFileKey}`
    if (prevKeysRef.current !== currentKeys) {
      prevKeysRef.current = currentKeys
      loadedDurationsRef.current = new Map()
      setDurationsLoaded(false)
    }
  }, [videoClipsKey, audioFileKey])

  useEffect(() => {
    if ((videoClips.length === 0 && audioClips.length === 0) || durationsLoaded) return
    const allPaths = [...videoClips.map((c) => c.path), ...audioClips.map((a) => a.path)]
    const allReady = allPaths.every((p) => preloader.getBlobUrl(p) !== null)
    if (!allReady) return

    let cancelled = false
    const run = async () => {
      const durations = new Map<string, number>()
      const videoPromises = videoClips.map(async (clip) => {
        const blobUrl = preloader.getBlobUrl(clip.path)
        if (!blobUrl) return
        const d = await new Promise<number>((resolve, reject) => {
          const v = document.createElement('video')
          v.preload = 'metadata'
          v.onloadedmetadata = () => resolve(v.duration)
          v.onerror = () => reject(new Error('video metadata failed'))
          v.src = blobUrl
        }).catch(() => null)
        if (d) durations.set(clip.id, d)
      })
      const audioPromises = audioClips.map(async (af) => {
        const blobUrl = preloader.getBlobUrl(af.path)
        if (!blobUrl) return
        const d = await new Promise<number>((resolve, reject) => {
          const a = new Audio()
          a.preload = 'metadata'
          a.onloadedmetadata = () => resolve(a.duration)
          a.onerror = () => reject(new Error('audio metadata failed'))
          a.src = blobUrl
        }).catch(() => null)
        if (d) durations.set(af.id, d)
      })
      await Promise.all([...videoPromises, ...audioPromises])
      if (!cancelled) {
        loadedDurationsRef.current = durations
        setDurationsLoaded(true)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [durationsLoaded, preloader, videoClips, audioClips])

  // Apply real durations once both video and audio are loaded.
  const clipDurationFingerprint = timelineState.clips
    .map((c) => `${c.id}:${c.originalDuration}`)
    .join(',')
  // RFC-1035: audio-only timelines (zero video clips, e.g. extract/split-audio
  // restored on reopen) must still get real durations applied and totalDuration
  // corrected — so track audio identity/duration too and rerun when it changes.
  const audioClipDurationFingerprint = timelineState.audioClips
    .map((c) => `${c.id}:${c.originalDuration}:${c.sourceNodeId ?? ''}:${c.sourcePath ?? ''}`)
    .join(',')

  useEffect(() => {
    if (!durationsLoaded || (timelineState.clips.length === 0 && timelineState.audioClips.length === 0)) return
    const durations = loadedDurationsRef.current
    if (durations.size === 0) return

    const current = timelineStateRef.current
    // RFC-1035 (RC2b): shared production helper applies real durations to video +
    // audio clips (audio routed through resolveAudioClipRealDuration; audio-only
    // timelines handled). Single source of truth shared with TimelineEditorModal.
    const result = applyRealDurationsToTimeline(current.clips, current.audioClips, durations)
    if (result.changed) {
      refreshTimelineState({
        ...current,
        clips: result.clips,
        audioClips: result.audioClips,
        totalDuration: result.totalDuration,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationsLoaded, clipDurationFingerprint, audioClipDurationFingerprint, refreshTimelineState])

  // ── Initialise from canonical node config or connected inputs ─────
  const savedTimelineConfig = useMemo(
    () =>
      timelineConfig &&
      isPristineTimelineRenderConfig(timelineConfig) &&
      (initialClips.length > 0 || initialAudioClips.length > 0)
        ? undefined
        : timelineConfig,
    [initialAudioClips.length, initialClips.length, timelineConfig],
  )
  const timelineConfigFingerprint = useMemo(
    () => (savedTimelineConfig ? JSON.stringify(savedTimelineConfig) : ''),
    [savedTimelineConfig],
  )

  useEffect(() => {
    if (!savedTimelineConfig && initialClips.length === 0 && initialAudioClips.length === 0) return
    if (savedTimelineConfig) {
      const restored = parseTimelineRenderConfig(savedTimelineConfig, initialClips, initialAudioClips, {
        connectedVideoSourceIds: new Set(initialClips.map((clip) => clip.sourceNodeId)),
        connectedAudioSourceIds: new Set(initialAudioClips.map((clip) => clip.sourceNodeId)),
      })
      const configHash = timelineConfigTraceHash(savedTimelineConfig)
      const [configSizeBytes, configDigest] = configHash.split(':')
      const dedupeKey = `${nodeId}:preview:${configHash}`
      if (!emittedPreviewConfigTraceKeys.has(dedupeKey)) {
        emittedPreviewConfigTraceKeys.add(dedupeKey)
        const timelineItemCount = restored.clips.length + restored.audioClips.length
        previewDurationLog({
          event: 'preview-restored-from-config',
          nodeId,
          savedClipCount: savedTimelineConfig.timeline?.clips?.length ?? 0,
          restoredClipCount: restored.clips.length,
          duration_ms: Math.round(restored.totalDuration * 1000),
          clipDurationsMs: restored.clips.map((clip) => Math.round(clip.effectiveDuration * 1000)),
        })
        emitTimelineSourceEvent({
          event: 'timeline-preview-applied-in-video-config',
          source: 'in_video_config',
          sourceField: 'in_video_config',
          consumer: 'preview',
          trigger: 'in_video_config_changed',
          configFound: true,
          configHash,
          configSizeBytes: Number(configSizeBytes),
          configDigest,
          configStatus: timelineItemCount === 0 ? 'found_empty' : 'found_with_items',
          loadResult: timelineItemCount === 0 ? 'found-empty-timeline' : 'applied-items',
          nodeId,
          readOnly,
          connectedVideoCount: initialClips.length,
          connectedAudioCount: initialAudioClips.length,
          savedClipCount: savedTimelineConfig.timeline?.clips?.length ?? 0,
          savedAudioClipCount: savedTimelineConfig.timeline?.audio_tracks?.length ?? 0,
          restoredClipCount: restored.clips.length,
          restoredAudioClipCount: restored.audioClips.length,
          savedConfigClipCount: savedTimelineConfig.timeline?.clips?.length ?? 0,
          savedConfigAudioClipCount: savedTimelineConfig.timeline?.audio_tracks?.length ?? 0,
          restoredUiClipCount: restored.clips.length,
          restoredUiAudioClipCount: restored.audioClips.length,
          uiClipCount: restored.clips.length,
          uiAudioClipCount: restored.audioClips.length,
          clipCount: restored.clips.length,
          audioClipCount: restored.audioClips.length,
          trackCount: restored.videoTrackCount + restored.audioTrackCount,
          timelineItemCount,
          duration_ms: Math.round(restored.totalDuration * 1000),
        })
      }
      restoreState(restored)
      return
    }

    const totalDuration = computeTimelineDuration(initialClips, initialAudioClips)
    previewDurationLog({
      event: 'preview-built-from-input-media',
      nodeId,
      connectedVideoCount: initialClips.length,
      duration_ms: Math.round(totalDuration * 1000),
      clipDurationsMs: initialClips.map((clip) => Math.round(clip.effectiveDuration * 1000)),
    })
    emitTimelineSourceEvent({
      event: 'timeline-preview-built-from-input-media',
      source: 'connected-input-media',
      consumer: 'preview',
      trigger: 'input_media_changed',
      configFound: false,
      nodeId,
      readOnly,
      connectedVideoCount: initialClips.length,
      connectedAudioCount: initialAudioClips.length,
      clipCount: initialClips.length,
      audioClipCount: initialAudioClips.length,
      timelineItemCount: initialClips.length + initialAudioClips.length,
    })
    restoreState({
      clips: initialClips,
      audioClips: initialAudioClips,
      playheadPosition: 0,
      zoomLevel: 50,
      isPlaying: false,
      selectedClipIds: new Set<string>(),
      totalDuration,
      videoTrackCount: 1,
      audioTrackCount: 1,
      copiedClip: null,
      copiedClipType: null,
      // RFC-0651
      mutedVideoTracks: new Set<number>(),
      mutedAudioTracks: new Set<number>(),
      magnetEnabled: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialClips, initialAudioClips, timelineConfigFingerprint, restoreState, nodeId, readOnly])

  // ── Playback hook ────────────────────────────────────────────────
  const { togglePlayPause: handleTogglePlayPause, playheadPositionRef } = useTimelinePlayback({
    timelineState,
    setPlayhead,
    togglePlayPause,
  })

  const toggleMute = useCallback(() => setIsMuted((m) => !m), [])
  const resumeAudioContexts = useCallback(() => {
    videoPreviewRef.current?.resumeAudioContexts()
    audioPool.resumeAudioContexts()
  }, [audioPool])

  // ── Active clip introspection for overlays ───────────────────────
  const activeClipIndex = useMemo(() => {
    const sorted = [...timelineState.clips].sort(
      (a, b) =>
        a.trackIndex - b.trackIndex ||
        a.startOffset - b.startOffset ||
        a.order - b.order,
    )
    const idx = sorted.findIndex(
      (c) =>
        timelineState.playheadPosition >= c.startOffset &&
        timelineState.playheadPosition < c.startOffset + c.effectiveDuration,
    )
    return idx === -1 ? null : idx
  }, [timelineState.clips, timelineState.playheadPosition])

  const activeClipName = useMemo(() => {
    if (activeClipIndex == null) return null
    const sorted = [...timelineState.clips].sort(
      (a, b) =>
        a.trackIndex - b.trackIndex ||
        a.startOffset - b.startOffset ||
        a.order - b.order,
    )
    return sorted[activeClipIndex]?.label ?? null
  }, [timelineState.clips, activeClipIndex])

  const controls: TimelinePreviewControls = {
    isPlaying: timelineState.isPlaying,
    togglePlayPause: handleTogglePlayPause,
    playheadPosition: timelineState.playheadPosition,
    totalDuration: timelineState.totalDuration,
    setPlayhead,
    currentClipIndex: activeClipIndex,
    currentClipName: activeClipName,
    videoCount: timelineState.clips.length,
    audioCount: timelineState.audioClips.length,
    isMuted,
    toggleMute,
    resumeAudioContexts,
    previewElementRef,
  }

  return (
    <div
      ref={previewElementRef}
      className="relative w-full h-full"
      data-testid="timeline-preview-player"
      data-audio-count={timelineState.audioClips.length}
      data-video-count={timelineState.clips.length}
      data-preloader-ready={preloader.isReady ? 'true' : 'false'}
      data-preloader-loading-count={preloader.loadingCount}
      data-preloader-error-paths={preloader.errorPaths.join(',') || 'none'}
      data-playhead-position={timelineState.playheadPosition.toFixed(3)}
      data-total-duration={timelineState.totalDuration.toFixed(3)}
      data-is-playing={timelineState.isPlaying ? 'true' : 'false'}
      data-muted={isMuted ? 'true' : 'false'}
    >
      <VideoPreviewPlayer
        textAspect={timelineState.textOverlays?.length ? (timelineState.textFormat ?? DEFAULT_TEXT_FORMAT).width / (timelineState.textFormat ?? DEFAULT_TEXT_FORMAT).height : undefined}
        textLayer={timelineState.textOverlays?.length ? <LegacyTextPreview document={{ overlays: timelineState.textOverlays, format: timelineState.textFormat ?? DEFAULT_TEXT_FORMAT }}
          playhead={timelineState.playheadPosition} positionRef={playheadPositionRef} selectedKey={null} disabled duration={timelineState.totalDuration} /> : undefined}
        surface="canvas"
        ownerNodeId={nodeId}
        ref={videoPreviewRef}
        clips={timelineState.clips}
        playheadPosition={timelineState.playheadPosition}
        playheadPositionRef={playheadPositionRef}
        isPlaying={timelineState.isPlaying}
        onVideoEnd={() => togglePlayPause()}
        preloader={preloader}
        globalMuted={isMuted}
        hideClipBadge={hideClipBadge}
      />
      {renderOverlays && renderOverlays(controls)}
    </div>
  )
}
